import React, { FC, memo, MouseEvent, useState } from 'react';
import TnkDialog from '@app/components/TnkDialog';
import { TypeModal } from '@app/modules/errors';
import classNames from 'classnames';
import { Button } from 'primereact/button';
import { DialogProps } from 'primereact/dialog';

const headerTitle = {
  /* SUCCESS */
  [TypeModal.NORMAL]: '正常',
  [TypeModal.WARNING]: '警告',
  /* CONFIRM */
  [TypeModal.INQUIRY]: '確認',
  [TypeModal.ERROR]: 'エラー',
  [TypeModal.INFO]: '確認',
  [TypeModal.NONE]: '',
};

const ICON_INFO_CIRCLE = 'pi-info-circle';

const icons = {
  /* SUCCESS */
  [TypeModal.NORMAL]: 'pi-check-circle',
  [TypeModal.WARNING]: 'pi-exclamation-triangle',
  /* CONFIRM */
  [TypeModal.INQUIRY]: ICON_INFO_CIRCLE,
  [TypeModal.ERROR]: ICON_INFO_CIRCLE,
  [TypeModal.INFO]: ICON_INFO_CIRCLE,
  [TypeModal.NONE]: '',
};

/**
 * Return a flag that indicate show/hide the cancel button
 * @param type type of dialog
 * @param showHide show/hide paramater
 * @returns boolean
 */
const displayCancelButton = (type: TypeModal, showCancelWarning = false): boolean => {
  switch (type) {
    // have cancel button
    case TypeModal.INQUIRY:
      return true;
    case TypeModal.WARNING:
      return showCancelWarning;
    // no cancel button
    case TypeModal.INFO:
    case TypeModal.NORMAL:
    case TypeModal.ERROR:
      return false;
    default:
      return false;
  }
};

export interface FooterProps {
  onYes: (e: MouseEvent<HTMLButtonElement>) => void;
  onNo: (e: MouseEvent<HTMLButtonElement>) => void;
  yesText?: string;
  noText?: string;
  type: TypeModal;
  visibleCancelWarning?: boolean;
}

const Footer = ({ onYes, onNo, yesText, noText, visibleCancelWarning, type }: FooterProps) => {
  return (
    <div className="popup-dialog-footer-container">
      <Button data-testid="yes-button" label={yesText} onClick={onYes} className="btn-yes mr-4" />
      {displayCancelButton(type, visibleCancelWarning) && (
        <Button
          data-testid="no-button"
          severity="secondary"
          label={noText}
          className="btn-no"
          onClick={onNo}
        />
      )}
    </div>
  );
};

export interface PopupDialogProps extends Omit<DialogProps, 'onHide'> {
  onYes: (e: MouseEvent<HTMLButtonElement>) => void;
  onNo?: () => void;
  content: string | React.ReactNode;
  yesText?: string;
  noText?: string;
  type?: TypeModal;
  visibleCancelWarning?: boolean;
}
const PopupDialog: FC<PopupDialogProps> = ({
  onNo,
  onYes,
  content,
  yesText = 'OK',
  noText = 'キャンセル',
  visibleCancelWarning,
  header,
  type = TypeModal.NONE,
  ...props
}) => {
  const [visible, setVisible] = useState(true);

  const onYesHandler = (e: MouseEvent<HTMLButtonElement>) => {
    onYes(e);
    setVisible(false);
  };
  const onNoHandler = () => {
    onNo?.();
    setVisible(false);
  };

  return (
    <TnkDialog
      blockScroll
      onHide={onNoHandler}
      header={header || headerTitle[type]}
      className={classNames('main-dialog', type)}
      draggable={false}
      footer={
        <Footer
          onYes={onYesHandler}
          onNo={onNoHandler}
          type={type}
          yesText={yesText}
          noText={noText}
          visibleCancelWarning={visibleCancelWarning}
        />
      }
      visible={visible}
      modal
      position="top"
      resizable={false}
      {...props}
    >
      <div className="content-block">
        {type !== TypeModal.NONE && <i className={`font-icon pi ${icons[type]}`} />}
        <div className="font-icon">{content}</div>
      </div>
    </TnkDialog>
  );
};

export default memo(PopupDialog);
